<template>
  <v-row>
    <v-col cols="12">
      <v-row>
        <v-col :class="$vuetify.breakpoint.xsOnly ? 'caption' : ''" cols="12">
          {{ $t('required_fields_are_marked_with_asterisk') }} (<span class="red--text">*</span>).
        </v-col>
      </v-row>
      <v-row>
        <v-col v-if="!type" cols="12">
          <v-card>
            <v-card-title>
              <SectionHeader>
                {{ $t('surveys.survey_type') }}<span class="red--text">*</span>
                <template v-slot:description>
                  {{ $t('surveys.choose_type_help') }}
                </template>
              </SectionHeader>
            </v-card-title>
            <v-radio-group v-model="survey.type" class="mt-0" hide-details>
              <v-card-text>
                <v-radio value="PERSONNEL">
                  <template v-slot:label>
                    <div class="ml-1">
                      <div class="font-weight-bold">{{ $t('surveys.type.PERSONNEL') }}</div>
                      <div class="caption mt-1">{{ $t('surveys.personnel_survey_explained') }}</div>
                      <div class="caption mt-2 font-weight-bold">{{ $t('surveys.personnel_survey_explained2') }}</div>
                    </div>
                  </template>
                </v-radio>
              </v-card-text>
              <v-card-text>
                <v-radio value="MANAGEMENT">
                  <template v-slot:label>
                    <div class="ml-1">
                      <div class="font-weight-bold">{{ $t('surveys.type.MANAGEMENT') }}</div>
                      <div class="caption mt-1">{{ $t('surveys.management_survey_explained') }}</div>
                      <div class="caption mt-2 font-weight-bold">{{ $t('surveys.management_survey_explained2') }}</div>
                    </div>
                  </template>
                </v-radio>
              </v-card-text>
              <v-card-text v-if="typeError">
                <v-alert :value="!!typeError" dense text type="error">
                  {{ typeError }}
                </v-alert>
              </v-card-text>
            </v-radio-group>
          </v-card>
        </v-col>
        <v-col cols="12">
          <v-card class="mt-5">
            <v-card-title>
              <SectionHeader>
                {{ $t('surveys.survey_settings') }}
                <template v-slot:description>
                  {{ $t('surveys.settings_help') }}
                </template>
              </SectionHeader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col class="mt-2" cols="12">
                  <div>
                    <strong>{{ $t('surveys.set_name_title') }}</strong>
                    <div class="caption">
                      {{ $t('surveys.set_name_desc') }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="survey.name"
                                :dense="$vuetify.breakpoint.xsOnly"
                                :label="$t('surveys.survey_name')"
                                autocomplete="off"
                                hide-details
                                outlined
                                persistent-placeholder
                  >

                  </v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-text class="mt-0">
              <v-row>
                <v-col cols="12">
                  <div>
                    <strong>{{ $t('surveys.schedule') }}<span class="red--text">*</span></strong>
                    <v-btn
                      :disabled="survey.dates.length === 0"
                      class="ml-2"
                      outlined
                      style="margin-top: -1px"
                      text
                      x-small
                      @click="survey.dates = []"
                    >{{ $t('clear') }}
                    </v-btn>
                    <div class="caption">
                      {{ $t('surveys.select_date_range') }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-date-picker v-model="survey.dates" full-width no-title range show-week></v-date-picker>
                </v-col>
                <v-col v-if="datesError" class="pt-0" cols="12">
                  <v-alert :value="!!datesError" dense text type="error">
                    {{ datesError }}
                  </v-alert>
                </v-col>
                <div class="ml-6 subtitle-2">
                  <span>{{ $t('surveys.chosen_dates') }}:</span> {{ $formatDateNoTime(survey.dates[0]) || '?' }} -
                  {{ $formatDateNoTime(survey.dates[1]) || '?' }}
                </div>
              </v-row>
            </v-card-text>
            <v-card-text>
              <survey-notification-select :survey="survey"
                                          @change="onSurveyNotificationChange"></survey-notification-select>
            </v-card-text>
            <v-card-text>
              <v-row>
                <v-col cols="12">
                  <div>
                    <strong>{{ $t('surveys.estimated_answerers') }}</strong>
                    <div class="caption">
                      {{ $t('surveys.estimated_answerers_explained') }}
                    </div>
                  </div>

                </v-col>
                <v-col cols="12" md="3" sm="6">
                  <v-text-field
                    v-model="survey.estimatedAnswerers"
                    :dense="$vuetify.breakpoint.xsOnly"
                    :label="$t('amount')"
                    :placeholder="$t('surveys.amount_example_placeholder')"
                    :rules="[$rules.positiveNumbersOnly]"
                    autocomplete="off"
                    min="0"
                    outlined
                    persistent-placeholder
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>
<script>
import SectionHeader from '@/components/SectionHeader';
import SurveyNotificationSelect from '@/components/SurveyNotificationSelect';
import dayjs from 'dayjs';
import surveyApi from '@/api/survey';

export default {
  name: 'create-survey',
  components: {SectionHeader, SurveyNotificationSelect},
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: null,
    },
    stayAfterSave: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      survey: {
        type: null,
        dates: [dayjs().format('YYYY-MM-DD'), dayjs().add(7, 'days').format('YYYY-MM-DD')],
        notifications: 'DAILY',
        estimatedAnswerers: null,
        name: null,
      },
      typeError: false,
      datesError: false,
      validationErrors: false,
    };
  },
  watch: {
    validationErrors(val) {
      this.$emit('validation-errors', val);
    },
  },
  methods: {
    async saveSurvey() {
      this.typeError = false;
      this.datesError = false;
      this.validationErrors = false;

      if (this.type) this.survey.type = this.type;

      if (!this.survey.type) {
        this.typeError = this.$t('surveys.errors.type_error');
        this.validationErrors = true;
      }
      if (this.survey.dates.length < 2) {
        this.datesError = this.$t('surveys.errors.dates_error');
        this.validationErrors = true;
      } else {
        const now = dayjs().format('YYYY-MM-DD');
        if (this.survey.dates.findIndex(d => dayjs(now).isAfter(dayjs(d))) > -1) {
          this.datesError = this.$t('surveys.errors.dates_in_past_error');
          this.validationErrors = true;
        }
      }
      if (this.validationErrors) {
        if (this.actionButtons?.save) {
          this.actionButtons.save.loading = false;
        }
        if (this.actionButtons?.cancel) {
          this.actionButtons.cancel.disabled = false;
        }
        return;
      }

      this.survey.dates = this.survey.dates?.sort((a, b) => {
        return dayjs(a).toDate() - dayjs(b).toDate();
      });

      const survey = {
        type: this.survey.type.toUpperCase(),
        start: dayjs(this.survey.dates[0]).set('hour', 0).set('minute', 0).set('second', 0).toISOString(),
        end: dayjs(this.survey.dates[1]).set('hour', 23).set('minute', 59).set('second', 59).toISOString(),
        notifications: this.survey.notifications,
        estimatedAnswerers: !this.survey.estimatedAnswerers ? null : parseInt(this.survey.estimatedAnswerers),
        name: this.survey.name
      };

      try {
        const response = await surveyApi.createSurvey(survey);
        if (this.stayAfterSave) {
          this.$emit('survey-saved', response);
        } else {
          this.$router.replace({name: 'tenant_survey_created', params: {id: response.key}});
        }
      } catch (e) {
        this.$handleApiError(e, this.$t('surveys.errors.create_error'));
      }
    },
    cancel() {
      this.$router.go(-1);
    },
    onSurveyNotificationChange(value) {
      if (this.survey) this.survey.notifications = value;
    },
  },
};
</script>

<style lang="scss">
.amount-input {
  input {
    text-align: center;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>
